import { Controller } from "@hotwired/stimulus"

/**
 * Swipe Actions Controller
 *
 * A reusable iOS-style swipe-to-reveal controller for list items.
 * Swiping left reveals action buttons behind the content.
 *
 * Usage:
 *   <div data-controller="swipe-actions"
 *        data-swipe-actions-threshold-value="60"
 *        data-swipe-actions-max-value="140">
 *     <div data-swipe-actions-target="actions" class="swipe-actions__buttons">
 *       <button data-action="click->swipe-actions#triggerAction"
 *               data-swipe-action-name="mute">Mute</button>
 *       <button data-action="click->swipe-actions#triggerAction"
 *               data-swipe-action-name="delete">Delete</button>
 *     </div>
 *     <div data-swipe-actions-target="content" class="swipe-actions__content">
 *       <!-- Your item content here -->
 *     </div>
 *   </div>
 *
 * Events dispatched:
 *   - "swipe-actions:action" with detail: { name, controller }
 *     Fired when an action button is tapped (after the swipe closes).
 *
 * CSS classes expected:
 *   - .swipe-actions          (host container, overflow: hidden)
 *   - .swipe-actions__buttons (positioned behind content)
 *   - .swipe-actions__content (foreground, will be translated)
 *   - .swipe-actions--open    (added when swiped open)
 */
export default class extends Controller {
  static targets = ["content", "actions"]
  static values = {
    threshold: { type: Number, default: 60 },  // px to snap open
    max:       { type: Number, default: 140 },  // max swipe distance
  }

  // Track the globally active instance so only one can be open at a time
  static activeInstance = null

  connect() {
    this.startX = 0
    this.startY = 0
    this.currentX = 0
    this.isSwiping = false
    this.isScrolling = false
    this.isOpen = false
    this.pointerDown = false

    this._onPointerDown = this.onPointerDown.bind(this)
    this._onPointerMove = this.onPointerMove.bind(this)
    this._onPointerUp = this.onPointerUp.bind(this)
    this._onExternalPointerDown = this.onExternalPointerDown.bind(this)
    this._onDragStart = this.onDragStart.bind(this)

    window.addEventListener("pointerup", this._onPointerUp, { passive: true })
    window.addEventListener("pointercancel", this._onPointerUp, { passive: true })

    if (this.hasContentTarget) {
      this.contentTarget.addEventListener("pointerdown", this._onPointerDown, { passive: true })
      this.contentTarget.addEventListener("pointermove", this._onPointerMove, { passive: true })
      this.contentTarget.addEventListener("dragstart", this._onDragStart)
    }

    document.addEventListener("pointerdown", this._onExternalPointerDown, { passive: true })
  }

  disconnect() {
    window.removeEventListener("pointerup", this._onPointerUp)
    window.removeEventListener("pointercancel", this._onPointerUp)

    if (this.hasContentTarget) {
      this.contentTarget.removeEventListener("pointerdown", this._onPointerDown)
      this.contentTarget.removeEventListener("pointermove", this._onPointerMove)
      this.contentTarget.removeEventListener("dragstart", this._onDragStart)
    }
    document.removeEventListener("pointerdown", this._onExternalPointerDown)

    if (this.constructor.activeInstance === this) {
      this.constructor.activeInstance = null
    }
  }

  onDragStart(e) {
    e.preventDefault()
  }

  // ── Touch handlers ──

  onPointerDown(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return // only left click
    
    // Close any other open instance
    if (this.constructor.activeInstance && this.constructor.activeInstance !== this) {
      this.constructor.activeInstance.close(true)
    }
    
    if (this.hasContentTarget) {
      try {
        this.contentTarget.setPointerCapture(e.pointerId)
      } catch (err) {}
    }

    this.startX = e.clientX
    this.startY = e.clientY
    this.currentX = 0
    this.isSwiping = false
    this.isScrolling = false
    this.pointerDown = true

    if (this.hasContentTarget) {
      this.contentTarget.style.transition = ""
    }
  }

  onPointerMove(e) {
    if (!this.pointerDown || this.isScrolling || !this.hasContentTarget) return

    const dx = e.clientX - this.startX
    const dy = e.clientY - this.startY

    // Determine scroll vs swipe on first significant move
    if (!this.isSwiping && !this.isScrolling) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 5) {
        this.isScrolling = true
        return
      }
      if (Math.abs(dx) > 5) {
        this.isSwiping = true
      }
    }

    if (!this.isSwiping) return

    let offset = dx
    if (this.isOpen) {
      offset = dx - this.maxValue
    }

    // Rubber-band effect at edges
    if (offset > 0) {
      offset = offset * 0.2
    } else if (offset < -this.maxValue) {
      offset = -this.maxValue - (Math.abs(offset) - this.maxValue) * 0.2
    }

    this.currentX = offset
    this.contentTarget.style.transform = `translateX(${offset}px)`
  }

  onPointerUp(e) {
    if (!this.pointerDown) return
    this.pointerDown = false
    if (this.hasContentTarget) {
      try {
        this.contentTarget.releasePointerCapture(e.pointerId)
      } catch (err) {}
    }
    
    if (!this.isSwiping || !this.hasContentTarget) return

    const easing = "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
    this.contentTarget.style.transition = easing

    if (this.currentX < -this.thresholdValue) {
      this.open()
    } else {
      this.close()
    }

    this.contentTarget.addEventListener("transitionend", () => {
      this.contentTarget.style.transition = ""
    }, { once: true })

    // On desktop (mouse), the browser fires a click event after mouseup.
    // Suppress it so a swipe doesn't accidentally navigate into the room.
    if (e.pointerType === 'mouse') {
      this._suppressNextClick()
    }

    this.isSwiping = false
  }

  onExternalPointerDown(e) {
    if (this.isOpen && !this.element.contains(e.target)) {
      this.close(true)
    }
  }

  /**
   * Swallows the very next click on this element so a mouse-based swipe
   * does not accidentally trigger the room-item's click handler.
   */
  _suppressNextClick() {
    const handler = (e) => {
      e.stopPropagation()
      e.preventDefault()
    }
    this.element.addEventListener('click', handler, { capture: true, once: true })
    // Safety: remove after a short timeout in case the click never fires
    setTimeout(() => this.element.removeEventListener('click', handler, { capture: true }), 400)
  }

  // ── Public API ──

  open() {
    if (!this.hasContentTarget) return
    this.contentTarget.style.transform = `translateX(-${this.maxValue}px)`
    this.element.classList.add("swipe-actions--open")
    this.isOpen = true
    this.constructor.activeInstance = this
  }

  close(animate = false) {
    if (!this.hasContentTarget) return
    if (animate) {
      this.contentTarget.style.transition = "transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)"
      this.contentTarget.addEventListener("transitionend", () => {
        this.contentTarget.style.transition = ""
      }, { once: true })
    }
    this.contentTarget.style.transform = ""
    this.element.classList.remove("swipe-actions--open")
    this.isOpen = false
    if (this.constructor.activeInstance === this) {
      this.constructor.activeInstance = null
    }
  }

  /**
   * Action handler for buttons inside the swipe actions panel.
   * Reads the action name from data-swipe-action-name on the clicked button,
   * closes the swipe, and dispatches a "swipe-actions:action" event.
   */
  triggerAction(e) {
    e.stopPropagation()
    const actionName = e.currentTarget.dataset.swipeActionName || "unknown"
    this.close(true)

    this.dispatch("action", {
      detail: { name: actionName },
      bubbles: true,
    })
  }
}
